<template>
  <div class="m-3">
    <AddNoteSlideover
      title="Add Note"
      :visible="slideoverVisibleAddNote"
      v-if="slideoverVisibleAddNote"
      v-on:toggle-slideover="toggleAddNoteSlideover"
      v-on:notes-changed="refreshNotes"
      :orderNumber="orderNumber"
    />
    <EditNoteSlideover
      title="Edit Note"
      :visible="slideoverVisibleEditNote"
      v-if="this.slideoverVisibleEditNote"
      v-on:toggle-slideover="toggleEditNoteSlideover"
      v-on:notes-changed="refreshNotes"
      :noteId="this.selectedNote.AutoNumber"
    />

    <div class="flex flex1 flex-row justify-between">
      <div class="pr-3 ml-1">
        <span v-if="selectedNote">{{ selectedNoteText }}</span>
        <span v-else>Select a note to view its contents here...</span>
      </div>
      <div class="relative inline-block text-left z-10" v-if="canShowActions">
        <div>
          <span class="rounded-md shadow-sm">
            <Button
              v-on:click-handler="toggleNotesMenu"
              class="btn btn-primary btn-small relative z-10"
            >
              Actions
              <icon
                set="solid"
                name="chevron-down"
                class="-mr-1 ml-2 h-5 w-5"
              />
            </Button>
            <button
              v-if="showNotesMenu"
              @click="showNotesMenu = false"
              tabindex="-1"
              class="fixed inset-0 w-full h-full bg-black opacity-50 cursor-default"
            ></button>
          </span>
        </div>

        <transition
          enter-active-class="ease-out duration-100"
          enter-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="ease-in duration-75"
          leave-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="showNotesMenu"
            class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg"
          >
            <div class="rounded-md bg-white shadow-xs">
              <div
                class="py-1"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="options-menu"
              >
                <button
                  class="menuItem"
                  role="menuitem"
                  @click="
                    toggleAddNoteSlideover();
                    toggleNotesMenu();
                  "
                >
                  Add Note
                </button>
                <button
                  class="menuItem"
                  role="menuitem"
                  v-bind:class="getDynamicMenuItemClass()"
                  :disabled="isNoteSelected"
                  @click="
                    toggleEditNoteSlideover();
                    toggleNotesMenu();
                  "
                >
                  Edit Note
                </button>
                <button
                  class="menuItem"
                  role="menuitem"
                  v-bind:class="getDynamicMenuItemClass()"
                  :disabled="isNoteSelected"
                  @click="
                    completeTask();
                    toggleNotesMenu();
                  "
                >
                  Complete Task
                </button>
                <button
                  class="menuItem"
                  role="menuitem"
                  v-bind:class="getDynamicMenuItemClass()"
                  :disabled="isNoteSelected"
                  @click="
                    cancelTask();
                    toggleNotesMenu();
                  "
                >
                  Cancel Task
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <div class="mt-3" style="height: 200px">
      <ag-grid-vue
        style="width: 100%; height: 100%"
        class="ag-theme-balham"
        :columnDefs="columnDefs"
        :rowData="rowData"
        :pagination="false"
        id="orderNotes"
        :gridOptions="gridOptions"
        @grid-ready="onGridReady"
        :context="context"
        :frameworkComponents="frameworkComponents"
        :defaultColDef="defaultColDef"
        :enableCellTextSelection="true"
        :ensureDomOrder="true"
      >
      </ag-grid-vue>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import { datetime } from "@/components/agGrid/cellRenderers";
import AddNoteSlideover from "@/views/Customers/components/slideovers/AddNote";
import EditNoteSlideover from "@/views/Customers/components/slideovers/EditNote";
import Button from "@/components/elements/Button.vue";
import { ApiFactory } from "@/api/ApiFactory.js";
import moneyFormatter from "@/mixins/moneyFormatter";
const NotesApi = ApiFactory.get("notes");

export default {
  name: "OrderNotes",
  props: {
    notes: {
      type: [Array, Object],
      required: true,
    },
    orderNumber: {
      type: [String, Number],
      required: true,
    },
    status: {
      required: false,
    },
    phase3d_care_app: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      columnDefs: null,
      rowData: null,
      pagination: false,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      overlayLoadingTemplate: true,
      showNotesMenu: false,
      selectedNote: false,
      slideoverVisibleAddNote: false,
      slideoverVisibleEditNote: false,
      selectedNoteText: "",
    };
  },
  computed: {
    isNoteSelected: function () {
      if (this.selectedNote.action_type_id || this.selectedNote.item_id) {
        return true;
      }
      return this.selectedNote === false;
    },
    filteredNotes() {
      let occurrences = {};

      return this.notes
        .filter((note) => {
          let identifier = `${note?.ItemNumber}_${note?.AlphaKey}`;

          if (
            !Object.prototype.hasOwnProperty.call(
              occurrences,
              `${identifier}-purchase`
            )
          ) {
            occurrences[`${identifier}-purchase`] = 0;
          }
          if (
            !Object.prototype.hasOwnProperty.call(
              occurrences,
              `${identifier}-care`
            )
          ) {
            occurrences[`${identifier}-care`] = 0;
          }

          if (note.Keywords?.includes("backorder-tool:purchase-note")) {
            return !occurrences[`${identifier}-purchase`]++;
          }

          if (note.Keywords?.includes("backorder-tool:care-note")) {
            return !occurrences[`${identifier}-care`]++;
          }

          return true;
        })
        .map((note) => {
          if (note.Keywords?.includes("backorder-tool:purchase-note")) {
            note.Notes = `Purchase Note (${note.AlphaKey}): ${note.Notes}`;
          }

          if (note.Keywords?.includes("backorder-tool:care-note")) {
            note.Notes = `Care Note (${note.AlphaKey}): ${note.Notes}`;
          }

          if (note?.action_type_id) {
            note.Notes = this.buildNote(note);
          }

          return note;
        });
    },
    canShowActions() {
      if (this.status?.Status === "Unprocessed") return false;
      return true;
    },
  },
  components: {
    AgGridVue,
    AddNoteSlideover,
    EditNoteSlideover,
    Button,
  },
  beforeMount() {
    this.gridOptions = {
      headerHeight: 30,
      rowHeight: 30,
      pagination: false,
      suppressPaginationPanel: true,
      rowSelection: "single",
      onSelectionChanged: this.onNoteSelectionChanged,
      rowDeselection: true,
      getRowStyle(params) {
        if (params.data.AssignedTo !== null && params.data.Status === null) {
          return { "background-color": "#0060fb", color: "white" };
        }
      },
      onRowClicked: (params) => {
        if (params.node.data.Notes) {
          this.selectedNoteText = params.node.data.Notes;
        } else {
          this.selectedNoteText = params.node.data.note;
        }
      },
    };

    this.context = { componentParent: this };
    this.defaultColDef = {
      sortable: true,
      filter: true,
      flex: 0,
      suppressMovable: false,
    };

    this.columnDefs = [
      {
        headerName: "Date",
        valueGetter: (params) => new Date(params.data.EntryDate),
        filter: "agDateColumnFilter",
        filterParams: {
          buttons: ["apply", "reset"],
          suppressAndOrCondition: true,
        },
        cellRenderer: "dateCell",
        width: 160,
        cellStyle: (params) => {
          if (params.data.Status === "Cancelled") {
            return { "text-decoration": "line-through solid black" };
          }
        },
        sort: "desc",
      },
      {
        headerName: "By",
        field: "EnteredBy",
        width: 120,
        cellStyle: (params) => {
          if (params.data.Status === "Cancelled") {
            return { "text-decoration": "line-through solid black" };
          }
        },
      },
      {
        headerName: "Note",
        field: "Notes",
        width: 400,
        cellStyle: (params) => {
          if (params.data.Status === "Cancelled") {
            return { "text-decoration": "line-through solid black" };
          }
        },
      },
      {
        headerName: "Event",
        field: "Event",
        width: 110,
        cellStyle: (params) => {
          if (params.data.Status === "Cancelled") {
            return { "text-decoration": "line-through solid black" };
          }
        },
      },
      {
        headerName: "Priority",
        field: "Priority",
        width: 110,
        cellStyle: (params) => {
          if (params.data.Status === "Cancelled") {
            return { "text-decoration": "line-through solid black" };
          }
        },
      },
      {
        headerName: "Follow Up",
        field: "FollowupAction",
        width: 130,
        cellStyle: (params) => {
          if (params.data.Status === "Cancelled") {
            return { "text-decoration": "line-through solid black" };
          }
        },
      },
      {
        headerName: "Assigned To",
        field: "AssignedTo",
        width: 140,
        cellStyle: (params) => {
          if (params.data.Status === "Cancelled") {
            return { "text-decoration": "line-through solid black" };
          }
        },
      },
      {
        headerName: "Completed",
        field: "Completed",
        width: 130,
        valueGetter: (params) => {
          return params.data.Completed === "1" ? "Yes" : "No";
        },
        cellStyle: (params) => {
          if (params.data.Status === "Cancelled") {
            return { "text-decoration": "line-through solid black" };
          }
        },
      },
    ];

    this.frameworkComponents = {
      dateCell: datetime,
    };
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
    this.rowData = this.filteredNotes;

    // this is a hacky way to fire sibling events
    this.$root.$on("orderRefreshNotes", () => {
      this.refreshNotes();
    });
  },
  methods: {
    onGridReady() {},
    toggleNotesMenu() {
      this.showNotesMenu = !this.showNotesMenu;
    },
    buildNote(note) {
      let adjustmentType = "";
      let adjustmentArray = [];
      if (note.reason?.includes("Edit Items")) {
        adjustmentType = "-";
      }
      adjustmentArray.push(
        `Reason: ${note.reason}, Policy: ${note.policy}, Action: ${
          note.action
        }, Amount: ${moneyFormatter.format(note.amount)}`
      );

      if (note?.adjustments_details?.length > 0) {
        let counter = 1;
        let adjustmentSkuArray = [];
        for (let item of note.adjustments_details) {
          adjustmentSkuArray.push(
            this.phase3d_care_app
              ? `(${counter}) Variant: ${item.variant_id} Quantity: ${adjustmentType}${item.quantity}`
              : `(${counter}) SKU: ${item.sku} Quantity: ${adjustmentType}${item.quantity}`
          );
          counter++;
        }
        adjustmentArray.push(` ${adjustmentSkuArray.join(", ")}`);
      }

      note.value ? adjustmentArray.push(` Comment: ${note.value}`) : "";
      note.additional_info
        ? adjustmentArray.push(` Additional Info: ${note.additional_info}`)
        : "";
      note.Notes = `${adjustmentArray.join(", ")}`;
      return note.Notes;
    },
    onNoteSelectionChanged() {
      const selectedRows = this.gridOptions.api.getSelectedRows();
      this.selectedNote =
        selectedRows[0] !== undefined ? selectedRows[0] : false;
    },
    getDynamicMenuItemClass() {
      return this.isNoteSelected ? "menuItemDisabled" : "menuItem";
    },
    toggleAddNoteSlideover() {
      this.slideoverVisibleAddNote = !this.slideoverVisibleAddNote;
    },
    toggleEditNoteSlideover() {
      this.slideoverVisibleEditNote = !this.slideoverVisibleEditNote;
    },
    async refreshNotes() {
      this.selectedNote = false;
      this.gridOptions.api.deselectAll();
      this.gridOptions.api.showLoadingOverlay();

      NotesApi.getNotesByOrderNumber(this.orderNumber)
        .then((results) => {
          this.$store.commit("orders/SET_ORDER_NOTES", results.data.body);
        })
        .finally(() => {
          this.rowData = this.filteredNotes;
          this.gridOptions.api.hideOverlay();
        });
    },
    async completeTask() {
      await NotesApi.completeTask(this.selectedNote.AutoNumber);
      await this.refreshNotes();
    },
    async cancelTask() {
      await NotesApi.cancelTask(this.selectedNote.AutoNumber);
      await this.refreshNotes();
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";

.menuItem {
  @apply block w-full px-4 py-2 text-sm leading-5 text-gray-700 text-left;
}

.menuItem:hover {
  @apply bg-gray-100 text-gray-900;
}

.menuItem:focus {
  @apply outline-none bg-gray-100 text-gray-900;
}

.menuItemDisabled {
  @apply menuItem text-gray-400;
}

.open-task {
  background-color: blue;
  color: white;
}
</style>
